import { sectionRenderer } from '@theme/section-renderer';

// The collection's original position is the tie-breaker, including undated pieces.
export function sortSoldProducts(products) {
  return products.map((product, position) => ({ ...product, position }))
    .sort((a, b) => (Number(b.sold_at) || 0) - (Number(a.sold_at) || 0) || a.position - b.position);
}

export function usesSoldDateOrder(url) {
  const sort = url.searchParams.get('sort_by');
  return /\/collections\/recently-sold(?:\/|$)/.test(url.pathname) && (!sort || sort === 'manual');
}

const requests = new Map();
const indexes = new Map();

function cleanURL(input) {
  const url = new URL(input, window.location.href);
  url.hash = '';
  for (const key of ['section_id', 'sections', 'view', 'page']) url.searchParams.delete(key);
  // 'manual' selects date order in the UI. Read the original collection order
  // underneath it so undated pieces keep the same order after filters/navigation.
  if (url.searchParams.get('sort_by') === 'manual') url.searchParams.delete('sort_by');
  url.searchParams.sort();
  return url;
}

async function fetchText(url) {
  const key = url.toString();
  if (!requests.has(key)) {
    if (requests.size >= 80) requests.delete(requests.keys().next().value);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    const pending = fetch(key, { credentials: 'same-origin', signal: controller.signal }).then((response) => {
      if (!response.ok) throw new Error(`Recently Sold request failed: ${response.status}`);
      return response.text();
    }).catch((error) => { requests.delete(key); throw error; }).finally(() => clearTimeout(timeout));
    requests.set(key, pending);
  }
  return requests.get(key);
}

async function mapPages(pages, render) {
  const output = new Array(pages.length);
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(4, pages.length) }, async () => {
    while (next < pages.length) {
      const index = next++;
      output[index] = await render(pages[index]);
    }
  }));
  return output;
}

export async function loadSoldIndex(input) {
  const base = cleanURL(input);
  const key = base.toString();
  if (!indexes.has(key)) {
    const pending = (async () => {
      const load = async (page) => {
        const url = new URL(base);
        url.searchParams.set('view', 'legacy-sold-index');
        url.searchParams.set('page', String(page));
        const data = JSON.parse(await fetchText(url));
        if (data.version !== 1 || data.handle !== 'recently-sold' || data.page !== page ||
            !Number.isInteger(data.pages) || data.pages < 0 || data.pages > 100 ||
            !Number.isInteger(data.count) || data.count < 0 || !Array.isArray(data.products)) {
          throw new Error('Invalid Recently Sold index.');
        }
        return data;
      };
      const first = await load(1);
      const products = [...first.products];
      for (let page = 2; page <= first.pages; page++) {
        const data = await load(page);
        if (data.count !== first.count || data.pages !== first.pages) throw new Error('Collection changed while loading.');
        products.push(...data.products);
      }
      const ids = products.map((product) => String(product.id));
      if (products.length !== first.count || new Set(ids).size !== ids.length ||
          ids.some((id) => !/^\d+$/.test(id)) ||
          products.some((product) => !Number.isFinite(Number(product.sold_at)) || Number(product.sold_at) < 0)) {
        throw new Error('Incomplete Recently Sold index.');
      }
      return sortSoldProducts(products);
    })().catch((error) => { indexes.delete(key); throw error; });
    indexes.set(key, pending);
  }
  return indexes.get(key);
}

function rawSectionURL(sectionId, input, page) {
  const url = cleanURL(input);
  url.searchParams.set('section_id', sectionId);
  url.searchParams.set('page', String(page));
  return url;
}

function rawSection(sectionId, input, page) {
  return fetchText(rawSectionURL(sectionId, input, page));
}

function parseHTML(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

function checkAborted(signal) {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
}

export async function renderSoldSection(sectionId, input, signal) {
  const url = new URL(input, window.location.href);
  const page = Math.max(1, Number.parseInt(url.searchParams.get('page') || '1', 10) || 1);
  const raw = await rawSection(sectionId, url, page);
  checkAborted(signal);
  if (!usesSoldDateOrder(url)) return raw;
  try {
    const doc = parseHTML(raw);
    const component = doc.querySelector('results-list[data-legacy-sold-collection]');
    const grid = component?.querySelector('[ref="grid"]');
    const pageSize = Number(component?.dataset.legacySoldPageSize);
    if (!grid || !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 250) return raw;
    const ordered = await loadSoldIndex(url);
    checkAborted(signal);
    if (!ordered.some((product) => Number(product.sold_at) > 0)) return raw;
    const selected = ordered.slice((page - 1) * pageSize, page * pageSize);
    if (!selected.length) return raw;
    const sourcePages = [...new Set(selected.map((product) => Math.floor(product.position / pageSize) + 1))];
    const documents = await mapPages(sourcePages, async (sourcePage) =>
      parseHTML(sourcePage === page ? raw : await rawSection(sectionId, url, sourcePage)));
    checkAborted(signal);
    const cards = new Map();
    for (const document of documents) {
      for (const card of document.querySelectorAll('[ref="grid"] > [data-product-id]')) {
        cards.set(card.dataset.productId, card);
      }
    }
    if (selected.some((product) => !cards.has(String(product.id)))) throw new Error('Missing Recently Sold card.');
    for (const card of grid.querySelectorAll(':scope > [data-product-id]')) card.remove();
    selected.forEach((product, index) => {
      const card = cards.get(String(product.id));
      card.dataset.page = String(page);
      card.className = card.className.replace(/product-grid__item--\d+/g, `product-grid__item--${index}`);
      grid.append(card);
    });
    return doc.documentElement.outerHTML;
  } catch (error) {
    if (signal?.aborted) throw error;
    console.warn('Legacy Recently Sold: keeping the collection order.', error);
    return raw;
  }
}

export async function initializeSoldCollection(component) {
  sectionRenderer.legacySoldOrderRenderer = renderSoldSection;
  const url = new URL(window.location.href);
  if (!usesSoldDateOrder(url)) return;
  const grid = component.querySelector('[ref="grid"]');
  if (!grid) return;
  const wrapper = component.closest('[id^="shopify-section-"]');
  if (wrapper && url.searchParams.get('sort_by') !== 'manual') {
    const page = Math.max(1, Number.parseInt(url.searchParams.get('page') || '1', 10) || 1);
    requests.set(rawSectionURL(component.getAttribute('section-id'), url, page).toString(), Promise.resolve(wrapper.outerHTML));
  }
  const visibility = grid.style.visibility;
  grid.style.visibility = 'hidden';
  component.setAttribute('aria-busy', 'true');
  try {
    const html = await renderSoldSection(component.getAttribute('section-id'), url);
    if (!component.isConnected || window.location.href !== url.href) return;
    const sorted = parseHTML(html).querySelector('[ref="grid"]');
    if (!sorted) return;
    grid.replaceChildren(...sorted.childNodes);
  } finally {
    grid.style.visibility = visibility;
    component.removeAttribute('aria-busy');
  }
}

function initializeGallery(card) {
  if (card.dataset.galleryReady === 'true') return;
  card.dataset.galleryReady = 'true';
  const slides = [...card.querySelectorAll('[data-card-slide]')];
  if (slides.length < 2) return;
  const count = card.querySelector('[data-card-count]');
  let index = 0;
  const show = (next) => {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    if (count) count.textContent = `${index + 1} / ${slides.length}`;
  };
  card.querySelector('[data-card-prev]')?.addEventListener('click', (event) => { event.preventDefault(); show(index - 1); });
  card.querySelector('[data-card-next]')?.addEventListener('click', (event) => { event.preventDefault(); show(index + 1); });
  let startX = null;
  card.querySelector('[data-card-gallery]')?.addEventListener('touchstart', (event) => {
    startX = event.touches[0]?.clientX ?? null;
  }, { passive: true });
  card.querySelector('[data-card-gallery]')?.addEventListener('touchend', (event) => {
    if (startX === null) return;
    const endX = event.changedTouches[0]?.clientX ?? startX;
    if (Math.abs(endX - startX) > 35) show(index + (endX < startX ? 1 : -1));
    startX = null;
  }, { passive: true });
}

const initializedHomes = new WeakSet();
export async function initializeSoldHome(home) {
  if (initializedHomes.has(home)) return;
  initializedHomes.add(home);
  const grid = home.querySelector('.legacy-product-grid');
  if (!grid) return;
  try {
    const base = new URL(home.dataset.legacySoldUrl, window.location.origin);
    // Preserve theme preview context when loading cards from a collection URL.
    const current = new URL(window.location.href);
    for (const key of ['preview_theme_id', '_ab', '_fd', 'pb']) {
      if (current.searchParams.has(key)) base.searchParams.set(key, current.searchParams.get(key));
    }
    const ordered = await loadSoldIndex(base);
    if (!ordered.some((product) => Number(product.sold_at) > 0)) return;
    const selected = ordered.slice(0, 8);
    const oldIds = [...grid.querySelectorAll('[data-home-product-card]')].map((card) => card.dataset.productId);
    if (selected.length === oldIds.length && selected.every((product, index) => String(product.id) === oldIds[index])) return;
    const pages = [...new Set(selected.map((product) => Math.floor(product.position / 8) + 1))];
    const documents = await mapPages(pages, async (page) => {
      const url = cleanURL(base);
      url.searchParams.set('view', 'legacy-sold-home-cards');
      url.searchParams.set('page', String(page));
      return parseHTML(await fetchText(url));
    });
    if (!home.isConnected) return;
    const cards = new Map();
    for (const document of documents) {
      for (const card of document.querySelectorAll('[data-legacy-sold-home-feed] > [data-home-product-card]')) {
        cards.set(card.dataset.productId, card);
      }
    }
    if (selected.some((product) => !cards.has(String(product.id)))) throw new Error('Missing Recently Sold home card.');
    const nodes = selected.map((product) => cards.get(String(product.id)));
    grid.replaceChildren(...nodes);
    nodes.forEach(initializeGallery);
  } catch (error) {
    initializedHomes.delete(home);
    console.warn('Legacy Recently Sold home: keeping the collection order.', error);
  }
}

function initializeHomes(scope = document) {
  if (scope.matches?.('[data-legacy-sold-home]')) initializeSoldHome(scope);
  scope.querySelectorAll('[data-legacy-sold-home]').forEach(initializeSoldHome);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeHomes(), { once: true });
} else {
  initializeHomes();
}
document.addEventListener('shopify:section:load', (event) => initializeHomes(event.target));
